/**
 * LO assignment for new applications (NP-415, revised by NP-560).
 *
 * Priority, first match wins:
 *  1. An `?lo=` in the CURRENT url — the borrower just followed a referral
 *     link, so it is a deliberate new referral and outranks any existing
 *     relationship.
 *  2. The Loan Officer on the borrower's most recent loan.
 *  3. `path_lo_email` in localStorage, but ONLY for a borrower with no loan
 *     yet — it bridges "arrived via ?lo=, navigated on, then created".
 *  4. null — the server assigns its default (Wizard:DefaultLoanOfficerEmail).
 *
 * NP-560: the stored capture used to outrank the borrower's own loan, so a
 * `?lo=` left in the browser by an earlier session — a shared QA machine, an
 * old visit — silently moved a returning borrower's next loan to a different
 * officer. A capture is evidence of how someone arrived once, never of who
 * their officer is; the loan itself is authoritative for that.
 *
 * Best-effort by design: a fetch failure never blocks loan creation.
 */

import { apiClient } from '@/services/api-client';
import { searchThroughReturnUrl } from '@/lib/returnUrlSearch';

/** Exported so tests assert against the real key rather than mirroring a literal. */
export const LO_EMAIL_STORAGE_KEY = 'path_lo_email';

/**
 * Review (NP-376..381): the stored value originates in a `?lo=` URL parameter, so
 * it is user-controllable. Every endpoint that takes `loanOfficerEmail` 400s on an
 * invalid address, so forwarding junk would turn a cosmetic branding miss into a
 * failed request. Anything that is not a plausible address is discarded, which
 * falls back to the server's own branch resolution.
 */
function plausibleEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Trimmed, lower-cased, and only if it looks like an address. */
export function normaliseLoEmail(raw: string | null | undefined): string | null {
  const value = raw?.trim().toLowerCase();
  if (!value || !plausibleEmail(value)) return null;
  return value;
}

/**
 * NP-547 — the loan-officer address carried by a URL, wherever the link buries it.
 *
 * QA hit `/login?returnUrl=%2F%3Flo%3Djweaver%40afncorp.com` — decoded,
 * `returnUrl=/?lo=jweaver@afncorp.com`. The `lo` is nested INSIDE `returnUrl`, so
 * every reader that asked the current URL for a top-level `lo` saw nothing and
 * captured nothing. In incognito, with no earlier capture to fall back on, the LO
 * was simply lost until after a successful login redirected to `/?lo=…` — by which
 * point registration and DBA theming had already run without it.
 *
 * `returnUrl` is followed because a login link legitimately wraps the destination,
 * and the e-consent hop re-wraps it again (`/e-consent?returnUrl=…`), so one level
 * is not always enough. The depth is bounded: a hand-crafted URL should not be able
 * to make this walk forever.
 *
 * The value stays untrusted either way — it is still run through
 * {@link normaliseLoEmail}, which is what keeps a junk `?lo=` out of loan creation.
 */
export function loEmailFromSearch(search: string | null | undefined): string | null {
  return searchThroughReturnUrl(search, (params) => normaliseLoEmail(params.get('lo')));
}

/**
 * Vesta's role label for the loan officer varies — "Loan Officer",
 * "LoanOfficer", "loan_officer" and "Loan Originator" all occur — so match on
 * the letters alone. Matching only "loan officer" misses the LO on live Vesta
 * loans, which reported "Loan Originator" and left NP-416's contact card
 * showing dead buttons. Mirrors the API's own IsLoanOfficerRole.
 */
export function isLoanOfficerRole(role: string | null | undefined): boolean {
  const normalised = role?.replace(/[^a-z]/gi, '').toLowerCase();
  return normalised === 'loanofficer' || normalised === 'loanoriginator';
}

/**
 * The loan officer's address out of a loan's contacts list. Shared by the
 * create-time resolver below and NP-505's theming so "which contact is the loan
 * officer" has one definition; the parameter type is the intersection of the
 * composite payload and the app-level LoanContact.
 */
export function findLoanOfficerEmail(
  contacts: ReadonlyArray<{ role?: string | null; email?: string | null }> | null | undefined,
): string | null {
  const lo = contacts?.find((c) => isLoanOfficerRole(c.role));
  return normaliseLoEmail(lo?.email);
}

export function getStoredLoEmail(): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(LO_EMAIL_STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
}

interface LoansListLite {
  loans?: Array<{ id?: string; loanId?: string }>;
}

/**
 * NP-546: the composite read returns every section under `sections`, keyed by the
 * camelCase section name — reading `contacts` off the root resolved undefined, so
 * this inheritance never fired and each succeeding loan fell to the server's
 * default LO. Keep the nesting; the flat shape silently disables the fallback.
 */
interface CompositeContactsLite {
  sections?: {
    contacts?: { contacts?: Array<{ role?: string; email?: string | null }> } | null;
  } | null;
}

/**
 * NP-376..NP-381 — the captured `?lo=` email ONLY, no network, for requests about
 * a loan that already exists (wizard submit, co-borrower invite).
 *
 * Deliberately not `resolveLoEmailForCreate`: that falls back to the LO on
 * `loans[0]`, which for an existing loan may be a DIFFERENT loan's officer. The
 * API already falls back to the loan's own Vesta branch when this field is
 * omitted, and that fallback is authoritative for that loan — so sending nothing
 * beats sending someone else's LO and mis-branding the email.
 *
 * The capture is the one thing the server cannot know: which DBA branch website
 * the borrower actually arrived through.
 */
export function readCapturedLoEmail(): string | null {
  // Delegates the read to getStoredLoEmail (NP-388, #153) — it already handles
  // SSR and the restricted-privacy-mode throw — and adds the validation this
  // ticket needs. One storage accessor, not two.
  return normaliseLoEmail(getStoredLoEmail());
}

/**
 * Caching the resolved officer is an optimisation, never the answer — storage
 * throws in restricted privacy mode and when the quota is full, and that must not
 * change which officer this function returns.
 */
function cacheLoEmail(email: string): void {
  try {
    localStorage.setItem(LO_EMAIL_STORAGE_KEY, email);
  } catch {
    /* storage unavailable */
  }
}

export async function resolveLoEmailForCreate(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  // 1. A referral link the borrower is standing on right now. Deliberate, and the
  //    only thing allowed to move an established relationship (NP-560 AC-2).
  const fromCurrentUrl = loEmailFromSearch(window.location.search);
  if (fromCurrentUrl) return fromCurrentUrl;

  let firstId: string | undefined;
  try {
    // retries: 0 + suppressToast: the resolver is awaited inline in the wizard
    // create flow; an optional lookup must not stack retry backoff onto loan
    // creation or surface a global error toast on failure.
    const list = await apiClient.get<LoansListLite>('/api/v1/loans', {
      retries: 0,
      suppressToast: true,
    });
    // The list is ordered most-recently-updated first (LoanReadRepository), so
    // loans[0] is "their most recent loan" as NP-560 AC-1 requires.
    firstId = list.loans?.[0]?.id ?? list.loans?.[0]?.loanId;
  } catch {
    // Whether this borrower has a loan is unknown, so the capture is kept as the
    // pre-NP-560 behaviour rather than dropping a genuine referral on a transient
    // failure.
    return readCapturedLoEmail();
  }

  // 3. No loan yet, so there is no relationship to preserve and the capture is
  //    still the one thing the server cannot know: which DBA branch website the
  //    borrower arrived through (NP-560 AC-4).
  if (!firstId) return readCapturedLoEmail();

  // 2. A returning borrower keeps their officer. Past this point the capture is
  //    never consulted — not when the loan has no officer, and not when this read
  //    fails — because reaching for it is the stale assignment NP-560 fixes
  //    (AC-1/AC-3). The scope is deliberate: an earlier, broader catch would have
  //    handed back the capture for a borrower already known to be returning.
  try {
    const composite = await apiClient.get<CompositeContactsLite>(
      `/api/v1/loans/${firstId}?include=${encodeURIComponent('contacts')}`,
      { retries: 0, suppressToast: true },
    );
    const email = findLoanOfficerEmail(composite.sections?.contacts?.contacts);
    if (email) cacheLoEmail(email);
    return email;
  } catch {
    return null;
  }
}
